/**
 * VIGÍA DE SUBIDA DE RANGO (cableado cross-módulo, Wave 2). Observa el ledger
 * `xp_event` [L] (que el SERVIDOR alimenta al procesar acciones reales: añadir
 * a colección, publicar, likes, amistad recíproca, login diario) y, cuando la
 * XP acumulada CRUZA un umbral de rango, dispara el modal de celebración
 * (screen #28 `modal_de_subida_de_rango`).
 *
 * Anti-falsos-positivos:
 *  - NO dispara en la hidratación inicial (el salto 0→N del primer sync no es
 *    un ascenso vivido): el primer rango observado por cuenta es la línea base.
 *  - Se resetea al cambiar de cuenta (multi-cuenta): cada userId re-ancla.
 */
import { createEffect, createSignal, Show } from "solid-js";
import { rankIndexForXp, RANKS } from "@famidex/shared";
import { useGamification } from "../hooks/use-gamification";
import { RankUpModal } from "./rank-up-modal";

export function RankUpWatcher() {
  const gam = useGamification();
  const [open, setOpen] = createSignal(false);
  const [celebratedIndex, setCelebratedIndex] = createSignal<number | null>(null);

  // Línea base por cuenta: índice de rango la primera vez que el ledger está
  // listo para este userId. `null` = aún sin anclar.
  let baselineUser: string | null = null;
  let baselineIndex: number | null = null;

  createEffect(() => {
    const uid = gam.userId();
    const pending = gam.isPending();
    const index = rankIndexForXp(gam.totalXp());

    if (!uid) {
      baselineUser = null;
      baselineIndex = null;
      return;
    }
    if (pending) {
      return;
    }
    if (baselineUser !== uid || baselineIndex === null) {
      baselineUser = uid;
      baselineIndex = index;
      return;
    }
    if (index > baselineIndex) {
      // Ascenso REAL observado en vivo → celebrar y re-anclar (si sube varios
      // rangos de golpe se celebra el más alto alcanzado).
      baselineIndex = index;
      setCelebratedIndex(index);
      setOpen(true);
    } else if (index < baselineIndex) {
      // El ledger puede compactarse/corregirse; nunca celebrar hacia abajo.
      baselineIndex = index;
    }
  });

  const rank = () => {
    const index = celebratedIndex();
    return index === null ? null : (RANKS[index] ?? null);
  };

  return (
    <Show when={rank()}>
      <RankUpModal open={open()} onOpenChange={setOpen} rank={rank()} mode="levelup" />
    </Show>
  );
}
